import { createMachine } from 'xstate';

import { KybDocumentType } from '__generated__/GQL';
import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import { VerificationStatus } from '../../Verification/lib/steps/types';
import getStatutesStatus from '../libs/getStatutesStatus';
import actions from './actions';
import guards from './guards';
import model, { Context } from './model';
import services from './services';
import { State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'statutes',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: ({ getModuleData }) => {
              const { company } = getModuleData().statutesData.viewer;

              const {
                kybDocuments,
                lastKybReview,
                profile: { legalForm },
              } = company;

              const status = getStatutesStatus({
                isAccountHolder: true,
                isCapitalDeposit: false,
                kybDocuments,
                lastKybReview,
                legalForm,
              });

              return (
                status === VerificationStatus.Completed ||
                status === VerificationStatus.Invalid
              );
            },
            target: State.REVIEW,
          },
          {
            target: State.INTRO,
          },
        ],
      },

      [State.INTRO]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.UPLOAD,
          PREV: State.ABORT,
        },
      },

      [State.UPLOAD]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.INTRO,
          SUBMIT_DOCUMENT_FILE: {
            actions: 'assignDocumentFile',
            target: State.CHECK,
          },
        },
      },

      [State.CHECK]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: {
            actions: shared.actions.track({
              name: 'Statutes_User_Self_Verification_Succeeded',
            }),
            target: State.SEND,
          },
          PREV: {
            actions: shared.actions.track({
              name: 'Statutes_User_Self_Verification_Failed',
            }),
            target: State.UPLOAD,
          },
        },
      },

      [State.SEND]: {
        invoke: {
          onDone: {
            target: State.REVIEW,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.UPLOAD,
          },
          src: 'sendDocument',
        },
      },

      [State.REVIEW]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT: State.DELETE,
          NEXT: [
            {
              cond: ({ getModuleData }) => {
                const statutesDocument =
                  getModuleData().statutesData.viewer.company.kybDocuments.find(
                    ({ type }) => type === KybDocumentType.Statutes,
                  );

                return Boolean(statutesDocument?.errorLocalized);
              },
              target: State.UPLOAD,
            },
            State.DONE,
          ],
          PREV: State.ABORT,
        },
      },

      [State.DELETE]: {
        invoke: {
          onDone: {
            target: State.INTRO,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: 'deleteDocument',
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
